import React, { useEffect, useMemo, useState } from 'react';
import { DocumentFolder } from '../api/documentApi';
import { useLanguage } from '../i18n/LanguageContext';
import ConfirmDialog from './ConfirmDialog';
import './DocumentExplorer.css';

// File-explorer style list view for documents: nested folders, sortable
// columns, folders-first/files-first/mixed grouping, drag-and-drop moves.

type SortBy = 'name' | 'updated' | 'created';
type SortDir = 'asc' | 'desc';
type Grouping = 'foldersFirst' | 'filesFirst' | 'mixed';

interface ExplorerPrefs {
    sortBy: SortBy;
    sortDir: SortDir;
    grouping: Grouping;
}

const PREFS_KEY = 'documentExplorerPrefs';
const EXPANDED_KEY = 'documentExplorerExpanded';

const loadPrefs = (): ExplorerPrefs => {
    try {
        const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
        return {
            sortBy: ['name', 'updated', 'created'].includes(saved.sortBy) ? saved.sortBy : 'name',
            sortDir: saved.sortDir === 'desc' ? 'desc' : 'asc',
            grouping: ['foldersFirst', 'filesFirst', 'mixed'].includes(saved.grouping)
                ? saved.grouping : 'foldersFirst',
        };
    } catch {
        return { sortBy: 'name', sortDir: 'asc', grouping: 'foldersFirst' };
    }
};

const loadExpanded = (): Set<number> => {
    try {
        const saved = JSON.parse(localStorage.getItem(EXPANDED_KEY) || '[]');
        return new Set(Array.isArray(saved) ? saved : []);
    } catch {
        return new Set();
    }
};

const formatDate = (dateString: string, locale: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const sameYear = date.getFullYear() === now.getFullYear();
    return date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        ...(sameYear ? {} : { year: 'numeric' }),
    });
};

// Pending destructive action, held while the confirmation dialog is up
type PendingDelete =
    | { kind: 'doc'; id: number; name: string }
    | { kind: 'docs'; ids: number[] }
    | { kind: 'folder'; folder: DocumentFolder; docCount: number; subfolderCount: number };

interface DocumentExplorerProps {
    documents: any[];
    folders: DocumentFolder[];
    flat?: boolean; // search mode: matching documents only, no folder tree
    onOpenDocument: (id: number) => void;
    onDeleteDocument: (id: number) => void | Promise<void>;
    onDeleteFolder: (id: number, deleteDocuments: boolean) => void | Promise<void>;
    onMoveDocument: (docId: number, folderId: number | null) => Promise<void> | void;
    onMoveFolder: (folderId: number, parentId: number | null) => Promise<void> | void;
    onCreateSubfolder: (parentId: number) => void;
    onCreateDocumentInFolder: (folderId: number) => void;
    onRenameDocument: (id: number, name: string) => Promise<void> | void;
}

const DocumentExplorer: React.FC<DocumentExplorerProps> = ({
    documents,
    folders,
    flat = false,
    onOpenDocument,
    onDeleteDocument,
    onDeleteFolder,
    onMoveDocument,
    onMoveFolder,
    onCreateSubfolder,
    onCreateDocumentInFolder,
    onRenameDocument,
}) => {
    const { t, language } = useLanguage();
    const [renamingId, setRenamingId] = useState<number | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [prefs, setPrefs] = useState<ExplorerPrefs>(loadPrefs);
    const [expanded, setExpanded] = useState<Set<number>>(loadExpanded);
    const [dragOverId, setDragOverId] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
    const [deleteContents, setDeleteContents] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    // Cmd/Ctrl-click selection, for deleting or moving several at once.
    // `anchorId` is where a shift-range measures from.
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [anchorId, setAnchorId] = useState<number | null>(null);

    useEffect(() => {
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    }, [prefs]);

    useEffect(() => {
        localStorage.setItem(EXPANDED_KEY, JSON.stringify(Array.from(expanded)));
    }, [expanded]);

    const toggleExpanded = (folderId: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
            } else {
                next.add(folderId);
            }
            return next;
        });
    };

    const setSort = (sortBy: SortBy) => {
        setPrefs((prev) => ({
            ...prev,
            sortBy,
            sortDir: prev.sortBy === sortBy && prev.sortDir === 'asc' ? 'desc' : 'asc',
        }));
    };

    const compare = useMemo(() => {
        const dir = prefs.sortDir === 'asc' ? 1 : -1;
        return (a: { name: string; updated_at: string; created_at: string }, b: typeof a) => {
            let result: number;
            if (prefs.sortBy === 'name') {
                result = a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
            } else {
                const key = prefs.sortBy === 'updated' ? 'updated_at' : 'created_at';
                result = new Date(a[key]).getTime() - new Date(b[key]).getTime();
            }
            return result * dir;
        };
    }, [prefs.sortBy, prefs.sortDir]);

    // One folder's children in display order. Rendering and shift-click range
    // selection both read from this, so a range can never disagree with what
    // is on screen.
    type Entry =
        | { kind: 'folder'; item: DocumentFolder }
        | { kind: 'doc'; item: any };

    const childEntries = (parentId: number | null): Entry[] => {
        const subfolders: Entry[] = folders
            .filter((f) => (f.parent ?? null) === parentId)
            .slice()
            .sort(compare)
            .map((f) => ({ kind: 'folder', item: f }));
        const docs: Entry[] = documents
            .filter((d) => (d.folder_id ?? null) === parentId)
            .slice()
            .sort(compare)
            .map((d) => ({ kind: 'doc', item: d }));

        if (prefs.grouping === 'mixed') {
            return [...subfolders, ...docs].sort((a, b) => compare(a.item, b.item));
        }
        return prefs.grouping === 'filesFirst' ? [...docs, ...subfolders] : [...subfolders, ...docs];
    };

    // Every document on screen, top to bottom — collapsed folders contribute
    // nothing, so a shift-range only ever covers rows the user can see.
    const visibleDocIds = useMemo(() => {
        if (flat) return documents.slice().sort(compare).map((d) => d.id);
        const ids: number[] = [];
        const walk = (parentId: number | null) => {
            childEntries(parentId).forEach((entry) => {
                if (entry.kind === 'doc') {
                    ids.push(entry.item.id);
                } else if (expanded.has(entry.item.id)) {
                    walk(entry.item.id);
                }
            });
        };
        walk(null);
        return ids;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents, folders, expanded, prefs.grouping, compare, flat]);

    // True when `candidateId` sits somewhere inside `ancestorId`'s subtree.
    const isDescendant = (candidateId: number, ancestorId: number): boolean => {
        const parentOf = new Map(folders.map((f) => [f.id, f.parent ?? null]));
        const seen = new Set<number>();
        let current = parentOf.get(candidateId) ?? null;
        while (current !== null && !seen.has(current)) {
            if (current === ancestorId) return true;
            seen.add(current);
            current = parentOf.get(current) ?? null;
        }
        return false;
    };

    const handleDropOn = async (e: React.DragEvent, targetFolderId: number | null) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverId(null);
        const dragged = e.dataTransfer.getData('documentIds');
        if (dragged) {
            // Sequential, not Promise.all: each move updates the same list in
            // the parent, and concurrent updates would overwrite one another.
            for (const id of JSON.parse(dragged) as number[]) {
                await onMoveDocument(id, targetFolderId);
            }
            clearSelection();
            return;
        }
        const docId = parseInt(e.dataTransfer.getData('documentId'), 10);
        if (docId) {
            await onMoveDocument(docId, targetFolderId);
            return;
        }
        const folderId = parseInt(e.dataTransfer.getData('folderId'), 10);
        if (folderId) {
            const currentParent = folders.find((f) => f.id === folderId)?.parent ?? null;
            if (targetFolderId === folderId || currentParent === targetFolderId) return;
            if (targetFolderId !== null && isDescendant(targetFolderId, folderId)) return;
            await onMoveFolder(folderId, targetFolderId);
            if (targetFolderId !== null) {
                setExpanded((prev) => new Set(prev).add(targetFolderId));
            }
        }
    };

    const allowDrop = (e: React.DragEvent, overId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverId(overId);
    };

    // A folder and everything nested under it — deleting cascades down, so the
    // confirmation has to count the whole subtree, not just direct children.
    const subtreeFolderIds = (rootId: number): number[] => {
        const childrenOf = new Map<number, number[]>();
        folders.forEach((f) => {
            const parent = f.parent ?? null;
            if (parent === null) return;
            childrenOf.set(parent, [...(childrenOf.get(parent) || []), f.id]);
        });
        const ids: number[] = [];
        const queue = [rootId];
        while (queue.length) {
            const current = queue.pop() as number;
            if (ids.includes(current)) continue;
            ids.push(current);
            queue.push(...(childrenOf.get(current) || []));
        }
        return ids;
    };

    const openFolderDeleteDialog = (folder: DocumentFolder) => {
        const ids = subtreeFolderIds(folder.id);
        setDeleteContents(false);
        setPendingDelete({
            kind: 'folder',
            folder,
            docCount: documents.filter((d) => ids.includes(d.folder_id ?? -1)).length,
            subfolderCount: ids.length - 1,
        });
    };

    const plural = (count: number, one: string, many: string) =>
        `${count} ${count === 1 ? one : many}`;

    const closeDialog = () => {
        if (isDeleting) return;
        setPendingDelete(null);
        setDeleteContents(false);
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setIsDeleting(true);
        try {
            if (pendingDelete.kind === 'doc') {
                await onDeleteDocument(pendingDelete.id);
            } else if (pendingDelete.kind === 'docs') {
                // Sequential for the same reason as a multi-document move
                for (const id of pendingDelete.ids) {
                    await onDeleteDocument(id);
                }
                clearSelection();
            } else {
                await onDeleteFolder(pendingDelete.folder.id, deleteContents);
            }
            setPendingDelete(null);
            setDeleteContents(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const renderDeleteDialog = () => {
        if (!pendingDelete) return null;

        if (pendingDelete.kind === 'doc') {
            return (
                <ConfirmDialog
                    title={t.document.deleteDocument}
                    message={t.document.confirmDeleteDocument.replace('{name}', pendingDelete.name)}
                    confirmLabel={isDeleting ? t.folder.deleting : t.document.delete}
                    cancelLabel={t.folder.cancel}
                    danger
                    busy={isDeleting}
                    onConfirm={confirmDelete}
                    onCancel={closeDialog}
                />
            );
        }

        if (pendingDelete.kind === 'docs') {
            return (
                <ConfirmDialog
                    title={t.document.deleteSelected}
                    message={t.document.confirmDeleteSelected
                        .replace('{count}', String(pendingDelete.ids.length))}
                    confirmLabel={isDeleting ? t.folder.deleting : t.document.delete}
                    cancelLabel={t.folder.cancel}
                    danger
                    busy={isDeleting}
                    onConfirm={confirmDelete}
                    onCancel={closeDialog}
                />
            );
        }

        const { folder, docCount, subfolderCount } = pendingDelete;
        const isEmpty = docCount === 0 && subfolderCount === 0;
        const contents = [
            docCount > 0 ? plural(docCount, t.folder.documentOne, t.folder.documentMany) : null,
            subfolderCount > 0
                ? plural(subfolderCount, t.folder.subfolderOne, t.folder.subfolderMany)
                : null,
        ].filter(Boolean).join(', ');

        return (
            <ConfirmDialog
                title={t.folder.deleteFolder}
                message={
                    isEmpty ? (
                        t.folder.confirmDeleteFolder.replace('{name}', folder.name)
                    ) : (
                        <>
                            <p>{t.folder.folderHolds.replace('{name}', folder.name).replace('{contents}', contents)}</p>
                            <p>
                                {deleteContents
                                    ? t.folder.alsoDeleteDocumentsHint
                                    : [
                                        docCount > 0 ? t.folder.documentsMoveToTop : null,
                                        subfolderCount > 0 ? t.folder.subfoldersRemoved : null,
                                    ].filter(Boolean).join(' ')}
                            </p>
                        </>
                    )
                }
                confirmLabel={isDeleting ? t.folder.deleting : t.folder.delete}
                cancelLabel={t.folder.cancel}
                danger
                busy={isDeleting}
                // Wiping the documents is the one path that needs a typed word
                requireTypedWord={deleteContents ? t.folder.delete : undefined}
                typePrompt={t.folder.typeToConfirm.replace('{word}', t.folder.delete.toUpperCase())}
                onConfirm={confirmDelete}
                onCancel={closeDialog}
            >
                {docCount > 0 && (
                    <label className="confirm-dialog-option">
                        <input
                            type="checkbox"
                            checked={deleteContents}
                            onChange={(e) => setDeleteContents(e.target.checked)}
                            disabled={isDeleting}
                        />
                        <span>
                            {t.folder.alsoDeleteDocuments.replace(
                                '{count}',
                                plural(docCount, t.folder.documentOne, t.folder.documentMany)
                            )}
                        </span>
                    </label>
                )}
            </ConfirmDialog>
        );
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
        setAnchorId(null);
    };

    // Documents deleted or moved out from under a selection must not linger in
    // it, or the count claims more than the list holds.
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const alive = new Set(documents.map((d) => d.id));
            const next = new Set(Array.from(prev).filter((id) => alive.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [documents]);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') clearSelection();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    /**
     * A click on a document row.
     *
     * Cmd/Ctrl adds or removes one, shift takes everything between it and the
     * last one clicked. A plain click keeps doing what it always did — open
     * the document — and drops any selection on the way, so there is no state
     * the list can be left in where clicking a document does nothing.
     */
    const handleDocClick = (e: React.MouseEvent, doc: any) => {
        if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(doc.id)) next.delete(doc.id);
                else next.add(doc.id);
                return next;
            });
            setAnchorId(doc.id);
            return;
        }

        if (e.shiftKey && anchorId !== null) {
            e.preventDefault();
            const from = visibleDocIds.indexOf(anchorId);
            const to = visibleDocIds.indexOf(doc.id);
            if (from !== -1 && to !== -1) {
                const [start, end] = from <= to ? [from, to] : [to, from];
                setSelectedIds(new Set(visibleDocIds.slice(start, end + 1)));
                return;
            }
        }

        clearSelection();
        onOpenDocument(doc.id);
    };

    const startRename = (doc: any) => {
        setRenamingId(doc.id);
        setRenameValue(doc.name);
    };

    const commitRename = async (doc: any) => {
        const name = renameValue.trim();
        setRenamingId(null);
        if (name && name !== doc.name) {
            await onRenameDocument(doc.id, name);
        }
    };

    const renderDocRow = (doc: any, depth: number) => {
        const isRenaming = renamingId === doc.id;
        const isSelected = selectedIds.has(doc.id);
        return (
        <div
            key={`doc-${doc.id}`}
            className={`explorer-row explorer-doc${isRenaming ? ' is-renaming' : ''}${isSelected ? ' is-selected' : ''}`}
            style={{ paddingLeft: `${12 + depth * 24}px` }}
            // Dragging a row whose name is being edited would cancel the edit
            draggable={!isRenaming}
            onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                // Dragging any row of a selection drags the whole selection;
                // dragging an unselected one leaves the selection alone and
                // takes just that document.
                if (isSelected && selectedIds.size > 1) {
                    e.dataTransfer.setData('documentIds', JSON.stringify(Array.from(selectedIds)));
                } else {
                    e.dataTransfer.setData('documentId', String(doc.id));
                }
            }}
            onClick={(e) => { if (!isRenaming) handleDocClick(e, doc); }}
        >
            <span className="explorer-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
            </span>
            {isRenaming ? (
                <input
                    className="explorer-rename-input"
                    value={renameValue}
                    autoFocus
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={() => commitRename(doc)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.currentTarget.blur();
                        } else if (e.key === 'Escape') {
                            // Clear first so the blur that follows saves nothing
                            setRenameValue(doc.name);
                            setRenamingId(null);
                        }
                    }}
                />
            ) : (
                <span className="explorer-name" title={doc.name}>{doc.name}</span>
            )}
            {doc.tag_details && doc.tag_details.length > 0 && (
                <span className="explorer-tags">
                    {doc.tag_details.slice(0, 3).map((tag: any) => (
                        <span
                            key={tag.id}
                            className="explorer-tag-dot"
                            title={tag.name}
                            style={{ backgroundColor: tag.color }}
                        />
                    ))}
                </span>
            )}
            <span className="explorer-date">{formatDate(doc.updated_at, language)}</span>
            {/* Appears on hover or keyboard focus; hidden while renaming */}
            {!isRenaming && (
                <button
                    className="explorer-action-btn explorer-rename-btn"
                    title={t.document.rename}
                    aria-label={`${t.document.rename}: ${doc.name}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        startRename(doc);
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
                    </svg>
                </button>
            )}
            <button
                className="explorer-action-btn"
                title={t.document.delete}
                onClick={(e) => {
                    e.stopPropagation();
                    setPendingDelete({ kind: 'doc', id: doc.id, name: doc.name });
                }}
            >
                🗑️
            </button>
        </div>
        );
    };

    const renderFolderRow = (folder: DocumentFolder, depth: number): React.ReactNode => {
        const subfolders = folders.filter((f) => (f.parent ?? null) === folder.id);
        const docs = documents.filter((d) => (d.folder_id ?? null) === folder.id);
        const isOpen = expanded.has(folder.id);
        const overKey = `folder-${folder.id}`;

        return (
            <React.Fragment key={overKey}>
                <div
                    className={`explorer-row explorer-folder ${dragOverId === overKey ? 'drag-over' : ''}`}
                    style={{ paddingLeft: `${12 + depth * 24}px` }}
                    draggable
                    onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('folderId', String(folder.id));
                    }}
                    onDragOver={(e) => allowDrop(e, overKey)}
                    onDragLeave={() => setDragOverId((prev) => (prev === overKey ? null : prev))}
                    onDrop={(e) => handleDropOn(e, folder.id)}
                    onClick={() => toggleExpanded(folder.id)}
                >
                    <span className={`explorer-disclosure ${isOpen ? 'open' : ''}`}>▸</span>
                    <span className="explorer-icon">{isOpen ? '📂' : '📁'}</span>
                    <span className="explorer-name" title={folder.name}>{folder.name}</span>
                    <span className="explorer-count">
                        {docs.length + subfolders.length > 0 ? docs.length + subfolders.length : ''}
                    </span>
                    <span className="explorer-date">{formatDate(folder.updated_at, language)}</span>
                    <button
                        className="explorer-add-doc-btn"
                        title={t.explorer.newDocumentInFolder}
                        aria-label={`${t.explorer.newDocumentInFolder}: ${folder.name}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCreateDocumentInFolder(folder.id);
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    </button>
                    <button
                        className="explorer-action-btn"
                        title={t.explorer.newSubfolder}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCreateSubfolder(folder.id);
                        }}
                    >
                        📁+
                    </button>
                    <button
                        className="explorer-action-btn"
                        title={t.folder.deleteFolder}
                        onClick={(e) => {
                            e.stopPropagation();
                            openFolderDeleteDialog(folder);
                        }}
                    >
                        🗑️
                    </button>
                </div>
                {isOpen && renderChildren(folder.id, depth + 1)}
                {isOpen && subfolders.length === 0 && docs.length === 0 && (
                    <div className="explorer-empty-folder" style={{ paddingLeft: `${12 + (depth + 1) * 24}px` }}>
                        {t.folder.noDocuments}
                    </div>
                )}
            </React.Fragment>
        );
    };

    const renderChildren = (parentId: number | null, depth: number): React.ReactNode =>
        childEntries(parentId).map((entry) =>
            entry.kind === 'folder'
                ? renderFolderRow(entry.item, depth)
                : renderDocRow(entry.item, depth)
        );

    const renderSelectionBar = () => {
        if (selectedIds.size === 0) return null;
        return (
            <div className="explorer-selection-bar">
                <span className="explorer-selection-count">
                    {t.document.selectedCount.replace('{count}', String(selectedIds.size))}
                </span>
                <span className="explorer-selection-hint">{t.document.dragSelectionHint}</span>
                <button className="explorer-selection-btn" onClick={clearSelection}>
                    {t.document.clearSelection}
                </button>
                <button
                    className="explorer-selection-btn danger"
                    onClick={() => setPendingDelete({ kind: 'docs', ids: Array.from(selectedIds) })}
                >
                    {t.document.deleteSelected}
                </button>
            </div>
        );
    };

    const sortArrow = (column: SortBy) =>
        prefs.sortBy === column ? (prefs.sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    if (flat) {
        const results = documents.slice().sort(compare);
        return (
            <div className="document-explorer">
                {renderSelectionBar()}
                <div
                className="explorer-body"
                // Clicking the empty space below the rows clears the selection,
                // the way it does in a file manager
                onClick={(e) => { if (e.target === e.currentTarget) clearSelection(); }}
            >
                    {results.map((doc) => renderDocRow(doc, 0))}
                </div>
                {renderDeleteDialog()}
            </div>
        );
    }

    return (
        <div
            className={`document-explorer ${dragOverId === 'root' ? 'drag-over-root' : ''}`}
            onDragOver={(e) => allowDrop(e, 'root')}
            onDragLeave={(e) => {
                if (e.currentTarget === e.target) setDragOverId(null);
            }}
            onDrop={(e) => handleDropOn(e, null)}
        >
            <div className="explorer-toolbar">
                <div className="explorer-header-row">
                    <button className="explorer-header-btn explorer-header-name" onClick={() => setSort('name')}>
                        {t.explorer.name}{sortArrow('name')}
                    </button>
                    <button className="explorer-header-btn explorer-header-date" onClick={() => setSort('updated')}>
                        {t.explorer.modified}{sortArrow('updated')}
                    </button>
                    <button className="explorer-header-btn explorer-header-date" onClick={() => setSort('created')}>
                        {t.explorer.created}{sortArrow('created')}
                    </button>
                    <select
                        className="explorer-grouping-select"
                        value={prefs.grouping}
                        onChange={(e) => setPrefs((prev) => ({ ...prev, grouping: e.target.value as Grouping }))}
                        title={t.explorer.grouping}
                    >
                        <option value="foldersFirst">{t.explorer.foldersFirst}</option>
                        <option value="filesFirst">{t.explorer.filesFirst}</option>
                        <option value="mixed">{t.explorer.mixed}</option>
                    </select>
                </div>
            </div>
            {renderSelectionBar()}
            <div
                className="explorer-body"
                // Clicking the empty space below the rows clears the selection,
                // the way it does in a file manager
                onClick={(e) => { if (e.target === e.currentTarget) clearSelection(); }}
            >
                {renderChildren(null, 0)}
                {folders.length === 0 && documents.length === 0 && (
                    <div className="explorer-empty">{t.folder.dragDropHint}</div>
                )}
            </div>
            {renderDeleteDialog()}
        </div>
    );
};

export default DocumentExplorer;
